import { Link, router } from "@inertiajs/react";
import { CheckCircle, ExternalLink, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Candidate, ExcludedFirm, PackageData, SubmissionRow, csrfToken, fmt } from "./_types";
import ExcludedPanel from "./ExcludedPanel";

interface Props {
  pkg: PackageData;
  candidate: Candidate;
  submissions: SubmissionRow[];
  excluded: ExcludedFirm[];
}

const STATUS_COLORS: Record<string, string> = {
  drafted:      "bg-gray-100 text-gray-600",
  submitted:    "bg-blue-100 text-blue-700",
  screening:    "bg-yellow-100 text-yellow-700",
  full_round:   "bg-purple-100 text-purple-700",
  offer_placed: "bg-green-100 text-green-700",
  rejected:     "bg-red-100 text-red-600",
  withdrawn:    "bg-gray-100 text-gray-500",
};

export default function SubmissionsSection({ pkg, candidate, submissions, excluded }: Props) {
  const reviewed = pkg.submissions_status === "approved" || pkg.submissions_status === "skipped";

  function markReviewed() {
    router.patch(`/candidates/${candidate.id}/package`, {
      package: { submissions_status: "approved" },
    });
  }

  function removeSubmission(packageSubmissionId: number) {
    router.delete(`/candidates/${candidate.id}/package/submission`, {
      data: { submission_id: packageSubmissionId, authenticity_token: csrfToken() },
    });
  }

  return (
    <Card className={reviewed ? "border-green-200" : ""}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {reviewed && <CheckCircle className="w-4 h-4 text-green-500" />}
            Submissions
            {submissions.length > 0 && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{submissions.length}</span>
            )}
          </CardTitle>
          {!reviewed && (
            <Button variant="outline" size="sm" onClick={markReviewed}>Mark Reviewed</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {submissions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            No draft submissions. Generation finds open jobs at eligible firms.
          </p>
        ) : (
          <div className="space-y-2">
            {submissions.map((s) => (
              <div key={s.package_submission_id} className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{s.firm_name}</span>
                    {s.job_id && s.job_title && (
                      <Link href={`/jobs/${s.job_id}`} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5">
                        {s.job_title} <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {s.submission_method === "portal_plus_email" ? "Portal + Email" : "Email only"} · Added {fmt(s.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[s.status] ?? "bg-gray-100 text-gray-600"}`}>
                    {s.status.replace(/_/g, " ")}
                  </span>
                  {!reviewed && (
                    <button
                      onClick={() => removeSubmission(s.package_submission_id)}
                      className="text-gray-300 hover:text-red-500 transition-colors"
                      title="Remove from package"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <ExcludedPanel excluded={excluded} />
      </CardContent>
    </Card>
  );
}
